import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt$ = (n) => "$" + Math.round(n || 0).toLocaleString();

export default function TimesheetsPanel({ engagementId, engagement, onEngagementUpdated }) {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    hours: "",
    rate: "",
    description: "",
    billable: true,
    user_id: "",
  });

  // Iter 85.62 · Prefill rate from the Engagement's Default Hourly Rate
  // each time the dialog opens. Advisor may still override the rate on
  // this one entry without mutating engagement.default_hourly_rate.
  useEffect(() => {
    if (!open) return;
    const engRate = engagement?.default_hourly_rate;
    setForm((f) => ({ ...f, rate: engRate ? String(engRate) : "" }));
  }, [open, engagement?.default_hourly_rate]);

  const load = async () => {
    const [e, s, u] = await Promise.all([
      api.get(`/timesheets?engagement_id=${engagementId}`),
      api.get(`/timesheets/summary/${engagementId}`),
      api.get("/users"),
    ]);
    setEntries(e.data);
    setSummary(s.data);
    setUsers(u.data);
    // Refresh engagement to pick up updated billed_to_date
    if (onEngagementUpdated) {
      const eng = await api.get(`/engagements/${engagementId}`);
      onEngagementUpdated(eng.data);
    }
  };

  useEffect(() => { load(); }, [engagementId]);

  const submit = async () => {
    if (!form.hours) return toast.error("Hours required");
    try {
      const payload = {
        engagement_id: engagementId,
        date: form.date,
        hours: parseFloat(form.hours),
        rate: form.rate ? parseFloat(form.rate) : null,
        description: form.description,
        billable: form.billable,
      };
      if (form.user_id) payload.user_id = form.user_id;
      await api.post("/timesheets", payload);
      setForm({ date: new Date().toISOString().slice(0, 10), hours: "", rate: "", description: "", billable: true, user_id: "" });
      setOpen(false);
      toast.success("Time logged");
      load();
    } catch { toast.error("Failed to log time"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this time entry?")) return;
    await api.delete(`/timesheets/${id}`);
    load();
  };

  const isRollupModel = engagement?.billing_model === "t_and_m" || engagement?.billing_model === "retainer";

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="border border-border bg-card p-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Total hours</div>
          <div className="mt-1 font-display text-2xl font-bold data-cell">{summary?.total_hours?.toFixed(1) || "0.0"}h</div>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Billable hours</div>
          <div className="mt-1 font-display text-2xl font-bold data-cell">{summary?.billable_hours?.toFixed(1) || "0.0"}h</div>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Billable value</div>
          <div className="mt-1 font-display text-2xl font-bold text-primary data-cell">{fmt$(summary?.billable_value)}</div>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Billing model</div>
          <div className="mt-1 font-display text-sm font-bold uppercase tracking-wider">{engagement?.billing_model || "—"}</div>
          <div className="text-[0.625rem] font-mono text-muted-foreground mt-1">
            {isRollupModel ? "Auto-rolls up to billed_to_date" : "Tracked only — billing set manually"}
          </div>
        </div>
      </div>

      {/* By user breakdown */}
      {summary?.by_user?.length > 0 && (
        <div className="border border-border bg-card">
          <div className="border-b border-border px-5 py-3">
            <h3 className="font-display font-bold text-sm">Time by team member</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left px-5 py-2 font-normal">Member</th>
                <th className="text-right px-3 py-2 font-normal">Hours</th>
                <th className="text-right px-3 py-2 font-normal">Billable hrs</th>
                <th className="text-right px-5 py-2 font-normal">Value</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_user.map((r) => (
                <tr key={r.user_id} className="border-b border-border/50">
                  <td className="px-5 py-2 font-medium">{r.user_name}</td>
                  <td className="px-3 py-2 text-right data-cell">{r.hours.toFixed(1)}h</td>
                  <td className="px-3 py-2 text-right data-cell">{r.billable_hours.toFixed(1)}h</td>
                  <td className="px-5 py-2 text-right data-cell text-primary">{fmt$(r.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Entries */}
      <div className="border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="font-display font-bold text-sm">Time entries ({entries.length})</h3>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-timesheet-btn">
                <Plus size={12} className="mr-1" /> Log time
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-card">
              <DialogHeader><DialogTitle className="font-display">Log time</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Date</Label>
                  <Input data-testid="timesheet-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none bg-secondary/40 mt-1" />
                </div>
                <div>
                  <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Hours</Label>
                  <Input data-testid="timesheet-hours" type="number" step="0.25" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="rounded-none bg-secondary/40 mt-1" />
                </div>
                <div>
                  <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Rate ($/hr, optional)</Label>
                  <Input type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="Default 250" className="rounded-none bg-secondary/40 mt-1" />
                </div>
                <div>
                  <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Team member</Label>
                  <Select value={form.user_id || "self"} onValueChange={(v) => setForm({ ...form, user_id: v === "self" ? "" : v })}>
                    <SelectTrigger className="rounded-none bg-secondary/40 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">(Me)</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Description</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none bg-secondary/40 mt-1" />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Checkbox id="billable" checked={form.billable} onCheckedChange={(v) => setForm({ ...form, billable: !!v })} className="rounded-none" data-testid="timesheet-billable" />
                  <label htmlFor="billable" className="text-xs cursor-pointer">Billable</label>
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="timesheet-submit" onClick={submit} className="rounded-none bg-primary text-primary-foreground">Log time</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left px-5 py-2 font-normal">Date</th>
              <th className="text-left px-3 py-2 font-normal">Member</th>
              <th className="text-left px-3 py-2 font-normal">Description</th>
              <th className="text-right px-3 py-2 font-normal">Hours</th>
              <th className="text-right px-3 py-2 font-normal">Rate</th>
              <th className="text-right px-3 py-2 font-normal">Value</th>
              <th className="text-right px-5 py-2 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan="7" className="px-5 py-8 text-center text-xs text-muted-foreground">No time logged yet.</td></tr>}
            {entries.map((t) => (
              <tr key={t.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`timesheet-row-${t.id}`}>
                <td className="px-5 py-2 data-cell text-xs">{t.date}</td>
                <td className="px-3 py-2 text-xs">{t.user_name}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-xs">
                  {t.description || "—"} {!t.billable && <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground border border-border ml-1 px-1">non-billable</span>}
                </td>
                <td className="px-3 py-2 text-right data-cell">{(t.hours || 0).toFixed(2)}h</td>
                <td className="px-3 py-2 text-right data-cell text-muted-foreground">{fmt$(t.rate || 250)}</td>
                <td className="px-3 py-2 text-right data-cell text-primary">{t.billable ? fmt$((t.hours || 0) * (t.rate || 250)) : "—"}</td>
                <td className="px-5 py-2 text-right">
                  <button onClick={() => del(t.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-timesheet-${t.id}`}><Trash size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
